<script lang="ts">
import { defineComponent, PropType, ref } from 'vue'
import SearchForm from '@/components/search/SearchForm.vue'
import SuggestTags from '@/components/search/SuggestTags.vue'
import { useSearchWordStore } from '@/stores/searchWord'

export default defineComponent({
  name: 'SearchWord',

  components: {
    SearchForm,
    SuggestTags,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    name: {
      type: String,
      default: '',
    },

    /**
     * フォームのサイズ
     */
    size: {
      type: String as PropType<'small' | 'normal' | 'large'>,
      default: 'normal',
    },

    /**
     * フォームの入力エリアの色
     */
    color: {
      type: String as PropType<'white' | 'gray'>,
      default: 'gray',
    },

    maxWidth: {
      type: String,
      default: '',
    },

    /**
     * 入力フォームを disabled にするかどうか
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * 入力フォームを readonly にするかどうか
     */
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'focus', 'blur', 'click:form'],

  setup(props, { emit }) {
    // store
    const searchWordStore = useSearchWordStore()

    const isShowSuggest = ref(false)

    const showSuggest = () => {
      emit('focus')

      if (searchWordStore.hasSuggest) {
        isShowSuggest.value = true
      }
    }

    const hideSuggest = () => {
      emit('blur')

      isShowSuggest.value = false
    }

    return {
      // store
      searchWordStore,

      // data
      isShowSuggest,

      // methods
      showSuggest,
      hideSuggest,
    }
  }
})
</script>

<template>
  <div class="l-unit__searchWord">
    <div
      class="l-unit__searchWord--description"
      :style="{
        'max-width': `${maxWidth || undefined}`
      }">
      <SearchForm
        v-if="searchWordStore.isValidForm"
        :form="searchWordStore.context.form"
        :model-value="modelValue"
        :name="name"
        :size="size"
        :color="color"
        :disabled="disabled"
        :readonly="readonly"
        placeholder="気になるワードで検索"
        @update:model-value="$emit('update:modelValue', $event)"
        @focus="showSuggest"
        @blur="hideSuggest"
        @click:form="$emit('click:form')" />
    <!-- /.l-unit__searchWord description --></div>

    <div
      :class="[
        'l-unit__searchWord--action',
        { 'is-active': isShowSuggest },
      ]">
      <dl
        v-if="searchWordStore.context.suggest.search.length"
        class="dl">
        <dt class="dt u-text__bold">
          よく検索されるワード
        </dt>
        <dd class="dd">
          <SuggestTags :items="searchWordStore.context.suggest.search" />
        </dd>
      </dl>
      <dl
        v-if="searchWordStore.context.suggest.access.length"
        class="dl">
        <dt class="dt u-text__bold">
          よく閲覧される用語
        </dt>
        <dd class="dd">
          <SuggestTags :items="searchWordStore.context.suggest.access" />
        </dd>
      </dl>
    <!-- /.l-unit__searchWord action --></div>
  <!-- /.l-unit__searchWord --></div>
</template>

<style lang="sass" scoped>
// layout
.l-unit__searchWord
  $root: &
  position: relative
  line-height: 1
  margin-bottom: 1.5em

  &:last-child
    margin-bottom: 0

  & &--description
    position: relative

  & &--action
    $margin-size: 4px
    width: 100%
    max-width: 520px
    position: absolute
    top: 100%
    left: 0
    transform: translateY(-8px) scale(0.98)
    font-size: $font-small
    margin-top: $margin-size
    padding: 1.7em
    background-color: rgba($bg-white, 1)
    border: 1px solid $bg-light-gray
    border-radius: $border-radius
    box-shadow: $box-shadow
    opacity: 0
    visibility: hidden
    z-index: 1
    +transition

    &.is-active
      transform: translateY(0) scale(1)
      opacity: 1
      visibility: visible

  +breakpoint($max-media: tablet)
    #{$root}--description
      max-width: none

    #{$root}--action
      display: none
</style>
